// Estado de la importación masiva para Mis productos (el botón de refresh y la rayita):
// ADR-2026-09-30-refresh-de-productos-en-mis-productos y ADR-2026-09-30-refresh-busca-en-todo-lo-conectado.
//
// El botón busca productos nuevos en TODO lo que la marca tenga conectado: Mercado Libre y
// Tiendanube. Cada origen tiene su propia importación en el backend (pueden correr a la vez); acá se
// juntan para mostrarlas como una sola cosa.

import { useCallback, useEffect, useRef, useState } from "react";
import { origenDisponible,
  buscarProductosNuevos,
  cancelarImportacion,
  enCurso,
  estadoDeImportacion,
  resumenDeUltima,
  type EstadoDeImportaciones,
  type OrigenDeImportacion,
  type VistaImportacion,
} from "../lib/importaciones";
import {
  estadoDeMercadoLibre,
  EVENTO_CAMBIO_DE_MERCADOLIBRE,
} from "../lib/mercadoLibreConexion";
import { getTnStatus } from "../lib/tiendaNube";
import {
  anotarCatalogoPendiente,
  AVISO_CATALOGO_NO_LLEGO,
  catalogoPendiente,
  EVENTO_CATALOGO_EN_CAMINO,
  EVENTO_CATALOGO_NO_LLEGO,
} from "../lib/traerCatalogoAlConectar";

/** Cada cuánto se pregunta mientras hay una importación en curso. */
const CADA_MS = 4000;
/** Cuánto queda a la vista el aviso ("No había productos nuevos" o un error). */
const AVISO_MS = 4000;

export type { OrigenDeImportacion };

export const NOMBRE_DEL_ORIGEN: Record<OrigenDeImportacion, string> = {
  mercadolibre: "Mercado Libre",
  tiendanube: "Tiendanube",
};

/** "Mercado Libre", "Mercado Libre y Tiendanube". */
export function nombresDeOrigenes(origenes: OrigenDeImportacion[]): string {
  const n = origenes.map((o) => NOMBRE_DEL_ORIGEN[o]);
  return n.length <= 1 ? (n[0] ?? "") : `${n.slice(0, -1).join(", ")} y ${n[n.length - 1]}`;
}

export interface AvisoDeImportacion {
  texto: string;
  tipo: "info" | "error";
}

type Estados = Partial<Record<OrigenDeImportacion, EstadoDeImportaciones>>;

export function useImportacionDeProductos(p: {
  companyId: string | null;
  brandId: string | null;
  /** false mientras la marca activa no se resolvió: no se pregunta nada. */
  listo: boolean;
}) {
  const [origenes, setOrigenes] = useState<OrigenDeImportacion[]>([]);
  const [estados, setEstados] = useState<Estados>({});
  const [pidiendo, setPidiendo] = useState(false);
  const [aviso, setAviso] = useState<AvisoDeImportacion | null>(null);
  const marcaRef = useRef(p.brandId);
  marcaRef.current = p.brandId;
  const origenesRef = useRef<OrigenDeImportacion[]>([]);
  origenesRef.current = origenes;
  /** Las importaciones que estaban en curso la última vez que se preguntó (por id). */
  const activasAntesRef = useRef<Set<string>>(new Set());
  const avisoTimerRef = useRef<number | null>(null);

  const mostrarAviso = useCallback((a: AvisoDeImportacion | null) => {
    if (avisoTimerRef.current !== null) window.clearTimeout(avisoTimerRef.current);
    avisoTimerRef.current = null;
    setAviso(a);
    if (a) {
      avisoTimerRef.current = window.setTimeout(() => setAviso(null), AVISO_MS);
    }
  }, []);

  const refrescar = useCallback(
    async (lista: OrigenDeImportacion[] = origenesRef.current) => {
      const marca = marcaRef.current;
      const pares = await Promise.all(
        lista.map(async (o) => [o, await estadoDeImportacion(marca, o)] as const),
      );
      if (marcaRef.current !== marca) return;
      const nuevos: Estados = {};
      for (const [o, e] of pares) if (e) nuevos[o] = e;
      setEstados((antes) => ({ ...antes, ...nuevos }));
      // Terminó alguna que estaba en curso. Si trajo productos, se ven en la grilla y en la campana:
      // no se repite. Se avisa sólo lo que si no, no se ve: que no había nada nuevo o que falló.
      const ahora = new Set<string>();
      for (const e of Object.values(nuevos)) if (enCurso(e.activa)) ahora.add(e.activa.id);
      for (const e of Object.values(nuevos)) {
        const u = e.ultima;
        if (!u || !activasAntesRef.current.has(u.id) || ahora.has(u.id)) continue;
        const fallo = u.estado === "fallida";
        const r = resumenDeUltima(u);
        if (r && (fallo || u.productos_nuevos === 0)) {
          const donde = lista.length > 1 ? `${NOMBRE_DEL_ORIGEN[u.origen]}: ` : "";
          mostrarAviso({ texto: `${donde}${r}`, tipo: fallo ? "error" : "info" });
        }
      }
      // Las que siguen en curso y las de orígenes que no se preguntaron esta vez quedan anotadas.
      const siguen = new Set(ahora);
      for (const id of activasAntesRef.current) {
        const deOtroOrigen = !Object.values(nuevos).some((e) => e.ultima?.id === id || e.activa?.id === id);
        if (deOtroOrigen) siguen.add(id);
      }
      activasAntesRef.current = siguen;
    },
    [mostrarAviso],
  );

  // Qué hay conectado y en qué va cada importación, al entrar y al cambiar de marca.
  const cargar = useCallback(async () => {
    const marca = marcaRef.current;
    const [ml, tn] = await Promise.all([
      estadoDeMercadoLibre(marca),
      getTnStatus(marca).catch(() => ({ connected: false })),
    ]);
    if (marcaRef.current !== marca) return;
    const conectados: OrigenDeImportacion[] = [];
    if (ml.disponible && ml.conectada) conectados.push("mercadolibre");
    // Tiendanube, para todos: conectar la tienda ya trae el catálogo por la importación masiva
    // (ADR-2026-10-05-integraciones-cierre, fase 2). Mercado Libre sigue detrás de su interruptor.
    // Si el backend todavía no la abrió para todos (un deploy en otro orden), no se muestra: fallaría al tocarla.
    if (tn.connected && (await origenDisponible(marca, "tiendanube"))) conectados.push("tiendanube");
    if (marcaRef.current !== marca) return;
    setOrigenes(conectados);
    origenesRef.current = conectados;
    if (conectados.length > 0) await refrescar(conectados);
    else setEstados({});
    // El catálogo de Tiendanube no llegó al conectar: se avisa con la salida, hasta que una búsqueda salga bien.
    if (conectados.includes("tiendanube") && catalogoPendiente(marca)) mostrarAviso({ texto: AVISO_CATALOGO_NO_LLEGO, tipo: "error" });
  }, [refrescar, mostrarAviso]);

  useEffect(() => {
    setOrigenes([]);
    setEstados({});
    setAviso(null);
    activasAntesRef.current = new Set();
    if (!p.companyId || !p.listo) return;
    void cargar();
  }, [p.companyId, p.brandId, p.listo, cargar]);

  // Conectar o desconectar Mercado Libre arranca o corta una importación: se pregunta enseguida.
  useEffect(() => {
    const alCambiar = () => {
      void cargar();
      window.setTimeout(() => void cargar(), 1500);
    };
    window.addEventListener(EVENTO_CAMBIO_DE_MERCADOLIBRE, alCambiar);
    // Conectar Tiendanube (desde cualquier pantalla) arranca su importación: el botón y el avance aparecen solos.
    window.addEventListener(EVENTO_CATALOGO_EN_CAMINO, alCambiar);
    window.addEventListener(EVENTO_CATALOGO_NO_LLEGO, alCambiar);
    return () => {
      window.removeEventListener(EVENTO_CAMBIO_DE_MERCADOLIBRE, alCambiar);
      window.removeEventListener(EVENTO_CATALOGO_EN_CAMINO, alCambiar);
      window.removeEventListener(EVENTO_CATALOGO_NO_LLEGO, alCambiar);
    };
  }, [cargar]);

  // Las que están en curso, en el orden de los orígenes. Mientras haya alguna, se pregunta seguido.
  const activas: VistaImportacion[] = origenes
    .map((o) => estados[o]?.activa)
    .filter((a): a is VistaImportacion => enCurso(a));
  const hayActivas = activas.length > 0;
  const claveActivas = activas.map((a) => a.id).join(",");
  useEffect(() => {
    if (!hayActivas) return;
    const t = window.setInterval(() => void refrescar(), CADA_MS);
    return () => window.clearInterval(t);
  }, [claveActivas, hayActivas, refrescar]);

  useEffect(
    () => () => {
      if (avisoTimerRef.current !== null) window.clearTimeout(avisoTimerRef.current);
    },
    [],
  );

  /** El botón de refresh: busca en todos los orígenes conectados. Uno que falla no frena a los otros. */
  const buscar = useCallback(async () => {
    if (pidiendo || hayActivas) return;
    setPidiendo(true);
    mostrarAviso(null);
    const errores: string[] = [];
    // Los orígenes son los de la marca del clic: si la persona cambia de marca a mitad de camino, se corta
    // en vez de buscar en la marca nueva con las tiendas de la anterior (review Greptile #156).
    const marca = marcaRef.current;
    try {
      for (const origen of origenes) {
        if (marcaRef.current !== marca) break;
        try {
          const imp = await buscarProductosNuevos(marca, origen);
          // Si termina antes del próximo vistazo (no había nada nuevo), igual se avisa.
          if (imp) activasAntesRef.current.add(imp.id);
          if (origen === "tiendanube") anotarCatalogoPendiente(marca, false);
        } catch (e) {
          const m = e instanceof Error ? e.message : "No pudimos buscar productos nuevos.";
          errores.push(origenes.length > 1 ? `${NOMBRE_DEL_ORIGEN[origen]}: ${m}` : m);
        }
      }
      // Con otra marca en pantalla, los errores de la anterior no se muestran.
      if (marcaRef.current !== marca) return;
      await refrescar();
      if (errores.length > 0) mostrarAviso({ texto: errores.join(" · "), tipo: "error" });
    } finally {
      setPidiendo(false);
    }
  }, [pidiendo, hayActivas, origenes, refrescar, mostrarAviso]);

  /** Corta todo lo que esté en curso. Lo que ya llegó queda. */
  const cancelar = useCallback(async () => {
    try {
      await Promise.all(activas.map((a) => cancelarImportacion(marcaRef.current, a.origen)));
      await refrescar();
    } catch (e) {
      mostrarAviso({
        texto: e instanceof Error ? e.message : "No se pudo cancelar.",
        tipo: "error",
      });
    }
  }, [activas, refrescar, mostrarAviso]);

  // Lo que la grilla necesita saber: cuándo cambió algo (para traer lo nuevo) y desde cuándo hay
  // productos esperando el análisis (para el cartel "Analizando producto").
  const firma = origenes
    .map((o) => {
      const a = estados[o]?.activa;
      return a && enCurso(a)
        ? `${a.id}:${a.procesados}:${a.analizados}:${a.analisis_fallidos}:${a.estado}`
        : `fin:${estados[o]?.ultima?.id ?? ""}`;
    })
    .join("|");
  const esperandoAnalisis = activas
    .filter((a) => a.analisis_total > a.analizados + a.analisis_fallidos)
    .map((a) => new Date(a.creada_at).getTime());
  const analizandoDesde = esperandoAnalisis.length > 0 ? Math.min(...esperandoAnalisis) : null;

  return {
    /** Hay algo conectado de dónde traer productos: el botón se muestra. */
    disponible: origenes.length > 0,
    origenes,
    /** Todas las que están en curso (una por origen). */
    activas,
    /** La primera en curso (para la rayita). */
    activa: activas[0] ?? null,
    /** El ícono gira: pidiendo o con una importación en curso. */
    trabajando: pidiendo || hayActivas,
    aviso,
    /** Cambia cada vez que avanza o termina una importación. */
    firma,
    analizandoDesde,
    buscar,
    cancelar,
    refrescar,
  };
}
